import { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronRight, Folder } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useCreateRequest } from '@/api/requests';
import { useOrganizations } from '@/api/organizations';
import { useRequestTemplates } from '@/api/requestTemplates';
import { useWorkspace } from '@/api/workspaces';
import { useFolders } from '@/api/folders';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import type { Folder as FolderType } from '@/types/api';

interface CreateRequestModalProps {
  open: boolean;
  onClose: () => void;
  /** Fixed organization (for client users whose user.organization_id is set).
   *  If null/undefined, the modal shows an org picker (for staff). */
  organizationId?: string | null;
  onCreated?: () => void;
}

/** Format a Date as YYYY-MM-DD in local time, suitable for <input type="date">. */
function toDateInputValue(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function CreateRequestModal({
  open,
  onClose,
  organizationId,
  onCreated,
}: CreateRequestModalProps) {
  const needsOrgPicker = !organizationId;
  const orgsQuery = useOrganizations();
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [targetFolderId, setTargetFolderId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const create = useCreateRequest();
  const toast = useToast();
  const { hasRole } = useAuth();

  const canUseTemplates = hasRole(['sysadmin', 'manager']);
  const [templatesExpanded, setTemplatesExpanded] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const templatesQuery = useRequestTemplates();

  // Folder picker state
  const [folderStack, setFolderStack] = useState<FolderType[]>([]);
  const [selectedFolderDisplay, setSelectedFolderDisplay] = useState('');

  // When the modal opens, default to the fixed org or clear the picker
  useEffect(() => {
    if (open) {
      setSelectedOrgId(organizationId ?? '');
      setTemplatesExpanded(false);
      setSelectedTemplateId('');
    }
  }, [open, organizationId]);

  const reset = () => {
    setTitle('');
    setInstructions('');
    setTargetFolderId('');
    setDueDate('');
    setSelectedTemplateId('');
    setFolderStack([]);
    setSelectedFolderDisplay('');
  };

  const effectiveOrgId = organizationId ?? selectedOrgId;
  const canSubmit = title.trim().length > 0 && !!effectiveOrgId;

  // Folder browser — fetches workspace + folders for the selected org
  const workspaceQuery = useWorkspace(effectiveOrgId || null);
  const workspaceId = workspaceQuery.data?.id ?? null;
  const allFoldersQuery = useFolders(workspaceId);
  const currentParentId = folderStack.length > 0 ? folderStack[folderStack.length - 1]?.id ?? null : null;
  const browseFolders = useMemo(() => {
    return (allFoldersQuery.data ?? []).filter((f) => (f.parent_id ?? null) === currentParentId && !f.is_archived);
  }, [allFoldersQuery.data, currentParentId]);

  const handleTemplatePick = (id: string) => {
    setSelectedTemplateId(id);
    if (!id) return;
    const tpl = (templatesQuery.data ?? []).find((t) => t.id === id);
    if (!tpl) return;
    setTitle(tpl.title);
    setInstructions(tpl.instructions ?? '');
    if (
      tpl.default_due_in_days !== null &&
      tpl.default_due_in_days !== undefined
    ) {
      const d = new Date();
      d.setDate(d.getDate() + tpl.default_due_in_days);
      setDueDate(toDateInputValue(d));
    }
    toast.info(`Pre-filled from template: ${tpl.name}`);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await create.mutateAsync({
        organization_id: effectiveOrgId,
        title: title.trim(),
        instructions: instructions.trim() || undefined,
        target_folder_id: targetFolderId.trim() || undefined,
        due_date: dueDate ? new Date(dueDate).toISOString() : undefined,
      });
      toast.success('Request created');
      reset();
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error('Failed to create request', apiError(e));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New file request"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={create.isPending}
            disabled={!canSubmit}
            className="w-auto"
          >
            Create request
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {canUseTemplates && (
          <div className="border border-slate-200 bg-slate-50 px-3 py-2">
            <button
              type="button"
              onClick={() => setTemplatesExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-medium text-bb-navy hover:underline"
              aria-expanded={templatesExpanded}
            >
              {templatesExpanded ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
              Load from template
            </button>
            {templatesExpanded && (
              <div className="mt-2">
                {templatesQuery.isLoading ? (
                  <p className="text-xs text-slate-500">Loading templates…</p>
                ) : templatesQuery.isError ? (
                  <p className="text-xs text-red-600">
                    {apiError(templatesQuery.error)}
                  </p>
                ) : (templatesQuery.data?.length ?? 0) === 0 ? (
                  <p className="text-xs text-slate-500">
                    No templates yet. Create one in Admin → Request templates.
                  </p>
                ) : (
                  <Select
                    value={selectedTemplateId}
                    onChange={handleTemplatePick}
                    options={(templatesQuery.data ?? []).map((t) => ({
                      value: t.id,
                      label: t.name,
                    }))}
                    placeholder="Select a template..."
                  />
                )}
                <p className="text-[11px] text-slate-500 mt-1">
                  Pre-fills the form below — you can still edit before creating.
                </p>
              </div>
            )}
          </div>
        )}
        <p className="text-xs text-slate-500">
          The client will see this on their dashboard. Make the title clear and the instructions specific.
        </p>
        {needsOrgPicker && (
          <div>
            <Label htmlFor="req-org">Organization</Label>
            <Select
              id="req-org"
              value={selectedOrgId}
              onChange={setSelectedOrgId}
              options={(orgsQuery.data ?? []).map((o) => ({
                value: o.id,
                label: o.name,
              }))}
              placeholder="Select an organization..."
            />
            {orgsQuery.isError && (
              <p className="text-xs text-red-600 mt-1">Failed to load organizations</p>
            )}
          </div>
        )}
        <div>
          <Label htmlFor="req-title">Title</Label>
          <Input
            id="req-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Q2 financial documents"
          />
        </div>
        <div>
          <Label htmlFor="req-instructions">Instructions</Label>
          <textarea
            id="req-instructions"
            rows={3}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Provide context or instructions for the client"
            className="w-full border border-bb-lilac bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-bb-navy/30 focus:border-bb-navy"
          />
        </div>
        <div>
          <Label>Target folder (optional)</Label>
          {!effectiveOrgId ? (
            <p className="text-xs text-slate-400 mt-1">Select an organization first to browse folders.</p>
          ) : workspaceQuery.isLoading || allFoldersQuery.isLoading ? (
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500"><Spinner size={14} /> Loading folders…</div>
          ) : (
            <div className="mt-1">
              {/* Breadcrumb */}
              <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                <button
                  type="button"
                  onClick={() => { setFolderStack([]); setTargetFolderId(''); setSelectedFolderDisplay(''); }}
                  className="hover:text-[#0410BD]"
                >
                  Workspace root
                </button>
                {folderStack.map((f, i) => (
                  <span key={f.id} className="flex items-center gap-1">
                    <ChevronRight size={12} />
                    <button
                      type="button"
                      onClick={() => {
                        const next = folderStack.slice(0, i + 1);
                        setFolderStack(next);
                        setTargetFolderId(f.id);
                        setSelectedFolderDisplay(f.name);
                      }}
                      className="hover:text-[#0410BD]"
                    >
                      {f.name}
                    </button>
                  </span>
                ))}
              </div>

              {/* Selected folder indicator */}
              {selectedFolderDisplay && (
                <div className="text-xs text-[#0410BD] font-medium mb-1">
                  Selected: {selectedFolderDisplay}
                  <button
                    type="button"
                    onClick={() => { setFolderStack([]); setTargetFolderId(''); setSelectedFolderDisplay(''); }}
                    className="ml-2 text-slate-400 hover:text-slate-600 underline"
                  >
                    clear
                  </button>
                </div>
              )}

              {/* Folder list */}
              {browseFolders.length > 0 ? (
                <div className="border border-[#E3E3F1] rounded-lg overflow-hidden max-h-36 overflow-y-auto">
                  {folderStack.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const next = folderStack.slice(0, -1);
                        setFolderStack(next);
                        if (next.length > 0) {
                          const parent = next[next.length - 1];
                          setTargetFolderId(parent.id);
                          setSelectedFolderDisplay(parent.name);
                        } else {
                          setTargetFolderId('');
                          setSelectedFolderDisplay('');
                        }
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-500 hover:bg-[#F2F2F8] border-b border-[#E3E3F1]"
                    >
                      ← Back
                    </button>
                  )}
                  {browseFolders.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => {
                        setFolderStack((s) => [...s, f]);
                        setTargetFolderId(f.id);
                        setSelectedFolderDisplay(f.name);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs border-b border-[#E3E3F1] last:border-0 transition-colors ${targetFolderId === f.id ? 'bg-[#EFF0FF] text-[#0410BD] font-medium' : 'text-slate-800 hover:bg-[#EFF0FF] hover:text-[#0410BD]'}`}
                    >
                      <Folder size={12} className="text-[#0410BD] shrink-0" />
                      {f.name}
                      <ChevronRight size={11} className="ml-auto text-slate-400" />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">
                  {folderStack.length > 0 ? 'No sub-folders here.' : 'No folders in this workspace.'}
                </p>
              )}
            </div>
          )}
        </div>
        <div>
          <Label htmlFor="req-due">Due date</Label>
          <Input
            id="req-due"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
